import {
  ContentCopyOutlined as ContentCopyOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
} from "@mui/icons-material";
import { FC, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import {
  UrlMappingTableDataRow_urlMapping,
  UrlMappingTableDataRow_urlMapping$key,
} from "~/src/__relay_artifacts__/UrlMappingTableDataRow_urlMapping.graphql";
import { Switch } from "~/src/components/common/Switch";
import {
  TableDataColumn,
  TableDataRow,
} from "~/src/components/common/tables/Table";
import { TableActions } from "~/src/components/common/tables/TableActions";
import { useOpenState } from "~/src/lib/hooks";

import { DeleteConfirmDialog } from "../../../global/DeleteConfirmDialog";
import {
  formatSourceSummary,
  matchTypeLabels,
  truncateText,
} from "../../UrlMappingForm/urlMappingFormUtils";

export type Props = {
  urlMappingRef: UrlMappingTableDataRow_urlMapping$key;
  siteSlug: string;
  connectionId: string;
  onDeleteClick: (urlMappingId: string, connectionId: string) => void;
  onActiveChange: (_: UrlMappingTableDataRow_urlMapping) => void;
  onDuplicateClick: (urlMappingId: string) => void;
};

const urlMappingFragment = graphql`
  fragment UrlMappingTableDataRow_urlMapping on UrlMapping {
    id
    name
    active
    matchType
    priority
    sourceUrl
    sourceHost
    sourcePath
    regexPattern
    destinationUrl
    conditions
  }
`;

export const UrlMappingTableDataRow: FC<Props> = ({
  urlMappingRef,
  siteSlug,
  connectionId,
  onDeleteClick,
  onActiveChange,
  onDuplicateClick,
}) => {
  const { isOpen, onOpen, onClose } = useOpenState();
  const urlMapping = useFragment(urlMappingFragment, urlMappingRef);

  const handleDeleteConfirm = useCallback(() => {
    onDeleteClick(urlMapping.id, connectionId);
    onClose();
  }, [connectionId, onClose, onDeleteClick, urlMapping.id]);

  const handleActiveChange = useCallback(
    () => onActiveChange(urlMapping),
    [onActiveChange, urlMapping]
  );

  const handleDuplicateClick = useCallback(
    () => onDuplicateClick(urlMapping.id),
    [onDuplicateClick, urlMapping.id]
  );

  const editTo = useMemo(
    () =>
      `/sites/${siteSlug}/url_mappings/${encodeURIComponent(
        urlMapping.id
      )}/edit`,
    [siteSlug, urlMapping.id]
  );

  const actionMenus = useMemo(
    () => [
      {
        label: "編集",
        icon: <EditOutlinedIcon />,
        to: editTo,
      },
      {
        label: "コピー",
        icon: <ContentCopyOutlinedIcon />,
        onClick: handleDuplicateClick,
      },
      {
        label: "削除",
        icon: <DeleteOutlineIcon />,
        onClick: onOpen,
      },
    ],
    [editTo, handleDuplicateClick, onOpen]
  );

  const sourceSummary = truncateText(formatSourceSummary(urlMapping));
  const destinationSummary = truncateText(urlMapping.destinationUrl);
  const displayName = urlMapping.name || "(名称なし)";

  return (
    <>
      <TableDataRow>
        <TableDataColumn type="text" data={displayName} />
        <TableDataColumn
          type="actions"
          data={
            <Switch
              name={`active-${urlMapping.id}`}
              isChecked={urlMapping.active}
              onChange={handleActiveChange}
            />
          }
        />
        <TableDataColumn
          type="text"
          data={
            matchTypeLabels[
              urlMapping.matchType as keyof typeof matchTypeLabels
            ] ?? urlMapping.matchType
          }
        />
        <TableDataColumn type="text" data={String(urlMapping.priority)} />
        <TableDataColumn type="text" data={sourceSummary} />
        <TableDataColumn type="text" data={destinationSummary} />
        <TableDataColumn
          type="actions"
          data={<TableActions actionMenus={actionMenus} />}
        />
      </TableDataRow>
      {isOpen && (
        <DeleteConfirmDialog
          name={displayName}
          isOpen={isOpen}
          onClose={onClose}
          onDelete={handleDeleteConfirm}
        />
      )}
    </>
  );
};
